// Helpers for assessment open/close times. The backend stores each as an absolute timestamp
// plus the IANA time zone the educator scheduled it in; we always display in that zone.

const FALLBACK_TIME_ZONE = 'UTC';

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

function resolveTimeZone(timeZone?: string | null): string {
  return timeZone && isValidTimeZone(timeZone) ? timeZone : getBrowserTimeZone();
}

export function listTimeZones(): string[] {
  const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: 'timeZone') => string[] })
    .supportedValuesOf;
  const zones = supportedValuesOf ? supportedValuesOf('timeZone') : [];
  return Array.from(new Set([...zones, getBrowserTimeZone(), FALLBACK_TIME_ZONE])).sort();
}

function parseTimestamp(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Converts a stored timestamp to the "YYYY-MM-DDTHH:mm" wall-clock value a datetime-local input expects. */
export function toZonedInputValue(value: string | null | undefined, timeZone?: string | null): string {
  const date = parseTimestamp(value);
  if (!date) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';

  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/** Formats a stored timestamp in its schedule time zone, e.g. "Oct 1, 2026, 9:00 AM EDT". */
export function formatScheduleDateTime(value?: string | null, timeZone?: string | null): string {
  const date = parseTimestamp(value);
  if (!date) return 'TBD';

  const zone = resolveTimeZone(timeZone);
  const dateTime = date.toLocaleString(undefined, {
    timeZone: zone,
    dateStyle: 'medium',
    timeStyle: 'short',
  } as Intl.DateTimeFormatOptions);

  return `${dateTime} ${timeZoneAbbreviation(date, zone)}`;
}

function timeZoneAbbreviation(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
  return parts.find((item) => item.type === 'timeZoneName')?.value ?? timeZone;
}

export function formatScheduleRange(
  opensAt?: string | null,
  closesAt?: string | null,
  timeZone?: string | null
): string {
  if (!opensAt && !closesAt) return 'Dates active: TBD';
  return `Dates active: ${formatScheduleDateTime(opensAt, timeZone)} - ${formatScheduleDateTime(closesAt, timeZone)}`;
}
